import { useTeamUuid } from '@/app/(dashboard)/hooks/useTeamUuid'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { UploadCrunchModal } from '@/app/(dashboard)/upload/UploadCrunchModal'
import { UploadCrunchProvider } from '@/app/(dashboard)/upload/UploadCrunchProvider'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Folder,
  Icon,
  IconColor,
  ProjectNew,
  Spinner,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { useGetDocumentTypesByOffice } from '@/dc-shared-web/src/hooks/api/office/useGetDocumentTypesByOffice'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'

import {
  faFileLines,
  faFolder,
  faPlus,
  faBriefcase,
} from '@document-crunch/pro-regular-svg-icons'
import { useRouter } from 'next/navigation'
import { useMemo, useState } from 'react'

export const DashboardHeaderDropdownMenu = ({
  onSelectItem,
  shouldShowCreateActionsMenu,
  canAddFolder,
  canAddProject,
  folder,
  project,
}: {
  onSelectItem?: (dialog: 'folder' | 'project' | null) => void
  shouldShowCreateActionsMenu: boolean
  canAddFolder: boolean
  canAddProject: boolean
  folder?: Folder | null
  project?: ProjectNew | null
}) => {
  const { trackEvent } = useFSTrackEvent()
  const [isOpen, setIsOpen] = useState(false)

  if (!shouldShowCreateActionsMenu) {
    return null
  }
  const showHorizontalDivider = canAddFolder || canAddProject

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          trackEvent(FS.BUTTON_CLICKED, {
            name: 'New popover opened',
          })
        }

        setIsOpen(isOpen)
      }}
    >
      <DropdownMenuComponent
        onSelectItem={onSelectItem}
        canAddFolder={canAddFolder}
        canAddProject={canAddProject}
        folder={folder}
        project={project}
        showHorizontalDivider={showHorizontalDivider}
        setIsOpen={setIsOpen}
      />
    </DropdownMenu>
  )
}

const DropdownMenuComponent = ({
  onSelectItem,
  canAddFolder,
  canAddProject,
  folder,
  project,
  showHorizontalDivider,
  setIsOpen,
}: {
  onSelectItem?: (dialog: 'folder' | 'project' | null) => void
  canAddFolder: boolean
  canAddProject: boolean
  folder?: Folder | null
  project?: ProjectNew | null
  showHorizontalDivider: boolean
  setIsOpen: (isOpen: boolean) => void
}) => {
  const { flags } = useLDFlags()
  const { trackEvent } = useFSTrackEvent()
  const router = useRouter()
  const [isUploadCrunchModalOpen, setIsUploadCrunchModalOpen] = useState(false)

  const teamUuid = useTeamUuid()

  const { data: documentTypes, isLoading: isLoadingDocumentTypes } =
    useGetDocumentTypesByOffice({
      officeUUID: teamUuid,
    })

  const hasDocumentTypesConfigured = useMemo(() => {
    return !!documentTypes && documentTypes.length > 0
  }, [documentTypes])

  const handleNewCrunch = () => {
    // Wait for document types to load before deciding which flow to use
    if (isLoadingDocumentTypes) {
      return
    }

    if (!flags.pd2UploadFlow10 || !hasDocumentTypesConfigured) {
      handleLegacyNewCrunch()
      return
    }

    setIsUploadCrunchModalOpen(true)
  }

  /* This method will be entirely removed once the new upload flow is fully launched */
  const handleLegacyNewCrunch = () => {
    trackEvent(FS.BUTTON_CLICKED, {
      name: 'New popover: New crunch',
    })
    setIsOpen(false)

    const folderIdQueryParam = folder?.id ? `folder=${folder.id}` : ''
    const projectIdQueryParam = project?.id ? `project=${project.id}` : ''
    const queryParams = [folderIdQueryParam, projectIdQueryParam]
      .filter(Boolean)
      .join('&')

    router.push(`/crunches/upload${queryParams ? `?${queryParams}` : ''}`)
  }

  const handleCloseUploadCrunchModal = () => {
    setIsUploadCrunchModalOpen(false)
  }

  const crunchInitialLocation = useMemo<LocationType>(() => {
    if (project?.id) {
      return {
        type: 'project',
        id: project.uuid,
        name: project.name,
      }
    }

    if (folder?.id) {
      return {
        type: 'folder',
        id: folder.uuid,
        name: folder.name,
      }
    }

    return {
      type: 'dashboard',
      id: 'dashboard',
      name: 'Dashboard',
    }
  }, [folder, project])

  return (
    <>
      <DropdownMenuTrigger
        className='tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-1 tw-rounded-md'
        data-testid='NewButtonPopover-btn'
        asChild
      >
        <Button
          variant='default'
          className='tw-flex tw-gap-2 tw-h-auto tw-py-2 tw-px-3 tw-font-medium tw-rounded-lg'
        >
          <Icon
            icon={faPlus}
            color={IconColor.white}
            className='tw-text-base'
          />
          New
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' sideOffset={0}>
        <div className='tw-flex tw-flex-col'>
          <DropdownMenuItem
            data-testid='NewButtonPopover-crunch-btn'
            onClick={handleNewCrunch}
            disabled={isLoadingDocumentTypes}
          >
            <div className='tw-flex tw-gap-2 tw-items-center tw-text-sm tw-py-0.5'>
              <div className='tw-w-5 tw-flex tw-justify-center'>
                {isLoadingDocumentTypes ? (
                  <Spinner className='tw-h-3 tw-w-3 tw-border-[2px]' />
                ) : (
                  <Icon
                    icon={faFileLines}
                    className='tw-text-sm'
                    color={IconColor.darkPrimary}
                  />
                )}
              </div>
              New crunch
            </div>
          </DropdownMenuItem>
          {showHorizontalDivider ? <DropdownMenuSeparator /> : null}
          {canAddFolder ? (
            <DropdownMenuItem
              data-testid='create-folder-dashboard-trigger'
              onClick={() => {
                trackEvent(FS.BUTTON_CLICKED, {
                  name: 'New popover: New folder',
                })
                onSelectItem?.('folder')
                setIsOpen(false)
              }}
            >
              <div className='tw-flex tw-gap-2 tw-items-center tw-text-sm tw-py-0.5'>
                <div className='tw-w-5 tw-flex tw-justify-center'>
                  <Icon
                    icon={faFolder}
                    className='tw-text-sm'
                    color={IconColor.darkPrimary}
                  />
                </div>
                New folder
              </div>
            </DropdownMenuItem>
          ) : null}
          {canAddProject ? (
            <DropdownMenuItem
              data-testid='create-project-dashboard-trigger'
              onClick={() => {
                trackEvent(FS.BUTTON_CLICKED, {
                  name: 'New popover: New project',
                })
                onSelectItem?.('project')
                setIsOpen(false)
              }}
            >
              <div className='tw-flex tw-gap-2 tw-items-center tw-text-sm tw-py-0.5'>
                <div className='tw-w-5 tw-flex tw-justify-center'>
                  <Icon
                    icon={faBriefcase}
                    className='tw-text-sm'
                    color={IconColor.darkPrimary}
                  />
                </div>
                New project
              </div>
            </DropdownMenuItem>
          ) : null}
        </div>
      </DropdownMenuContent>
      {isUploadCrunchModalOpen && (
        <UploadCrunchProvider>
          <UploadCrunchModal
            onClose={handleCloseUploadCrunchModal}
            crunchInitialLocation={crunchInitialLocation}
            teamUuid={teamUuid}
          />
        </UploadCrunchProvider>
      )}
    </>
  )
}
